import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  CalendarDays,
  Search
} from 'lucide-react';
import {fetchEvents,updateEvent, deleteEvent,createEvent} from "@/services/adminService"
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

const EventManagement = () => {
  const disableDateFrom = new Date().toISOString().split('T')[0];

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    event_type: 'company'
  });
  const { can } = usePermission();
  const { canCreate, canEdit, canDelete } = createCanHelpers(can);

  const canCreateEvent = canCreate("event_management");
  const canEditEvent = canEdit("event_management");
  const canDeleteEvent = canDelete("event_management");

  useEffect(() => {
    fetchAllEvents();
  }, []);

  const fetchAllEvents = async () => {
    try {
      setLoading(true);
      const response = await fetchEvents();
      // const response = await axios.get(`${API}/events`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        description: event.description || '',
        date: event.date,
        event_type: event.event_type || 'company'
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        date: '',
        event_type: 'company'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      date: '',
      event_type: 'company'
    });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.date) {
      toast.error('Title and date are required');
      return;
    }

    setSaving(true);
    try {
      if (editingEvent) {
        // Update existing event
        // await axios.put(`${API}/events/${editingEvent.id}`, formData);
        const response = await updateEvent(editingEvent._id, formData)
        toast.success('Event updated successfully');
      } else {
        // Create new event
        // await axios.post(`${API}/events`, formData);
        const response = await createEvent(formData)
        toast.success('Event created successfully');
      }
      
      fetchAllEvents();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;

    try {
      // await axios.delete(`${API}/events/${}`);
        const response = await deleteEvent(eventToDelete._id);
      toast.success('Event deleted successfully');
      fetchAllEvents();
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      company: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      team: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      milestone: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    return colors[type] || colors.other;
  };

  const getEventIcon = (type) => {
    const icons = {
      company: '🏢',
      team: '👥',
      milestone: '🎯',
      other: '📅'
    };
    return icons[type] || icons.other;
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort events by date (upcoming first)
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    return new Date(a.date) - new Date(b.date);
  });

  // Separate upcoming and past events
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = sortedEvents.filter(e => new Date(e.date) >= today);
  const pastEvents = sortedEvents.filter(e => new Date(e.date) < today);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Event Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Manage company events and milestones
          </p>
        </div>
        <Button
          onClick={() => {
            if (!canCreate("event_management", true)) return;
            handleOpenDialog();
          }}
          className={
            !canCreateEvent
              ? "opacity-70 cursor-not-allowed hover:bg-primary items-center"
              : "flex items-center space-x-2"
          }
        >
          <Plus className="h-4 w-4" />
          <span>Create Event</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Events
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {events.length}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <CalendarDays className="h-6 w-6 text-blue-600 dark:text-blue-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Upcoming
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {upcomingEvents.length}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-green-600 dark:text-green-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This Month
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {
                    events.filter((e) => {
                      const eventDate = new Date(e.date);
                      return (
                        eventDate.getMonth() === today.getMonth() &&
                        eventDate.getFullYear() === today.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📅</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Past Events
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {pastEvents.length}
                </p>
              </div>
              <div className="h-12 w-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📚</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Upcoming Events</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : upcomingEvents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                No upcoming events
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Event
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Date
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Description
                  </TableHead>
                  <TableHead className="text-right text-gray-700 dark:text-gray-300">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingEvents.map((event) => (
                  <TableRow
                    key={event.id}
                    className="border-b border-gray-200 dark:border-gray-700"
                  >
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">
                          {getEventIcon(event.event_type)}
                        </span>
                        <span className="font-medium dark:text-gray-200">
                          {event.title}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="dark:text-gray-300">
                      {new Date(event.date).toDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={getEventTypeColor(event.event_type)}>
                        {event.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="dark:text-gray-300">
                      {event.description ? (
                        <span className="text-sm">
                          {event.description.substring(0, 50)}
                          {event.description.length > 50 ? "..." : ""}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">
                          No description
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!canEdit("event_management", true)) return;
                            handleOpenDialog(event);
                          }}
                          className={
                            !canEditEvent
                              ? `opacity-70 cursor-not-allowed hover:bg-transparent`
                              : "hover:bg-blue-100 dark:hover:bg-blue-900"
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!canDelete("event_management", true)) {
                              return;
                            }
                            setEventToDelete(event);
                            setDeleteDialogOpen(true);
                          }}
                          className={
                            !canDeleteEvent
                              ? `opacity-70 cursor-not-allowed hover:bg-transparent`
                              : "hover:bg-red-100 dark:hover:bg-red-900"
                          }
                        >
                          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>📚</span>
              <span>Past Events</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Event
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Date
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Description
                  </TableHead>
                  <TableHead className="text-right text-gray-700 dark:text-gray-300">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pastEvents.map((event) => (
                  <TableRow
                    key={event.id}
                    className="opacity-60 border-b border-gray-200 dark:border-gray-700"
                  >
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">
                          {getEventIcon(event.event_type)}
                        </span>
                        <span className="font-medium dark:text-gray-200">
                          {event.title}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="dark:text-gray-300">
                      {new Date(event.date).toDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={getEventTypeColor(event.event_type)}>
                        {event.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="dark:text-gray-300">
                      {event.description ? (
                        <span className="text-sm">
                          {event.description.substring(0, 50)}
                          {event.description.length > 50 ? "..." : ""}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">
                          No description
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (!canDelete("event_management", true)) {
                            return;
                          }
                          setEventToDelete(event);
                          setDeleteDialogOpen(true);
                        }}
                        className={
                          !canDeleteEvent
                            ? `opacity-70 cursor-not-allowed hover:bg-primary`
                            : "hover:bg-red-100 dark:hover:bg-red-900"
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? "Edit Event" : "Create New Event"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g., Annual Company Picnic"
              />
            </div>

            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                min={disableDateFrom}
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
              />
            </div>

            <div>
              <Label htmlFor="event_type">Event Type</Label>
              <Select
                value={formData.event_type}
                onValueChange={(value) =>
                  setFormData({ ...formData, event_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">🏢 Company</SelectItem>
                  <SelectItem value="team">👥 Team</SelectItem>
                  <SelectItem value="milestone">🎯 Milestone</SelectItem>
                  <SelectItem value="other">📅 Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Event details..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingEvent ? (
                "Update Event"
              ) : (
                "Create Event"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete "{eventToDelete?.title}"? This
            action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventManagement;
